import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { ButtonSize } from "@slhuicore/button";
import { LitElement, html, TemplateResult, PropertyValues, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { SlhLoadingBar } from "@slhui/loading-bar";
import { style } from "./assets/file-input-item.generated-style";
import * as Constants from "./file-input-item.constants";
import { UploadedFile } from "./file-input-item.type";

/**
 * @CustomElement slh-file-input-item
 * @cssprop --slh-default-font-family - Font family. Error font family.
 * @cssprop --slh-base-semi-bold-font-weight - Font weight.
 * @cssprop --slh-base-xs-font-size - Font size.
 * @cssprop --slh-base-xxs-font-size - Error text font size.
 * @cssprop --slh-base-xxs-line-height - Font line heght.
 * @cssprop --slh-color-error - Error icon fill color. Error text color.
 * @cssprop --slh-color-success - Complited icon fill color.
 * @cssprop --slh-color-neutral-shade-80 - File input item font color.
 *
 * @slot default - File name
 */
@customElement("slh-file-input-item")
export default class SlhFileInputItem extends LitElement {
  /**
   * Used for setting id attribute value to the element, if not already set
   * @private
   */
  static instanceCount = 0;

  static styles = style;

  /**
   * File attribute
   */
  @property({
    type: Object,
    attribute: true,
  })
  file?: File | UploadedFile;

  /**
   * Defines percentage loaded
   */
  @property({
    type: Number,
    attribute: true,
    reflect: true,
  })
  percentage: number = 0;

  /**
   * Adds error styling
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  error: boolean = false;

  /**
   * Adds error text styling
   */
  @property({
    type: String,
    attribute: "error-text",
    reflect: true,
  })
  errorText: string = "";

  /**
   * Adds completed styling
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  completed: boolean = false;

  /**
   * Adds progressible styling
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  progressible: boolean = false;

  /**
   * Adds url
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  url?: string;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("file-input-item-wrapper");

    if (this.file && !(this.file instanceof File)) {
      this.completed = this.file.completed;
      this.error = this.file.error;
      this.errorText = this.file.errorText;
      this.url = this.file.url;
    }
  }

  /**
   * Initially set id, if not already set
   * @param {PropertyValues} changedProperties
   */
  protected firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);
    if (!this.hasAttribute("id")) {
      this.id = `slh-file-input-item-${(SlhFileInputItem.instanceCount += 1)}`;
    }
  }

  /**
   * @returns {ClassInfo}
   */
  renderStateIconClasses(): ClassInfo {
    return {
      completed: this.completed,
      error: this.error,
    };
  }

  /**
   * @returns {string} render html template
   */
  render(): TemplateResult {
    return html` <div class="file-input-item-holder">
      ${this.renderDownloadButton()}
      <div class="file-input-item-wrapper">
        <div class="file-input-item">
          ${!this.progressible ? this.renderStateIcon() : nothing}
          <slot></slot>
          <span class="remove-icon">${this.renderIcon()}</span>
        </div>
        ${this.progressible ? this.renderLoadingBar() : nothing}
        ${this.errorText ? this.renderErrorText() : nothing}
      </div>
    </div>`;
  }

  /**
   * @returns render download button
   */
  renderDownloadButton(): TemplateResult {
    if (this.url && this.url !== "") {
      return html`<slh-link
        href="${this.url}"
        target="_blank"
        class="file-input-item-download"
        size="${ButtonSize.SM}"
        secondary
      >
        <slh-icon type="${IconType.ICO_DOWNLOAD}"></slh-icon>
      </slh-link>`;
    }
    return html``;
  }

  /**
   * @returns render icon
   */
  renderIcon(): TemplateResult {
    return html`<slh-icon
      @click="${this.removeEvent}"
      tabindex="0"
      focusable="true"
      type="${IconType.ICO_SMALL_CLOSE}"
    ></slh-icon>`;
  }

  /**
   * @returns render icon
   */
  renderStateIcon(): TemplateResult {
    return html`<slh-icon
      tabindex="0"
      class="file-input-item-state-icon ${classMap(
        this.renderStateIconClasses()
      )}"
      type="${this.getStateIcon()}"
    ></slh-icon>`;
  }

  /**
   * @returns render error text
   */
  renderErrorText(): TemplateResult {
    return html`<span class="file-input-item-error-text"
      >${this.errorText}</span
    >`;
  }

  private getStateIcon(): IconType {
    if (this.error) {
      return IconType.ICO_MISSINGINFO;
    }
    if (this.completed) {
      return IconType.ICO_PASSED;
    }
    return IconType.ICO_PROGRESS_NONE;
  }

  /**
   * @returns {string} render html template
   */
  renderLoadingBar(): HTMLElement {
    const loadingBar = document.createElement(
      "slh-loading-bar"
    ) as SlhLoadingBar;
    loadingBar.success = !this.error;
    loadingBar.error = this.error;
    loadingBar.percentage =
      this.error || this.completed ? 100 : this.percentage;

    return loadingBar;
  }

  /**
   * @fires "remove-file"
   */
  private removeEvent(event: Event) {
    if (event instanceof CustomEvent) {
      if (this.file instanceof File) {
        this.dispatchFileRemove();
      } else {
        this.dispatchUploadedFileRemove();
      }
    }
  }

  /**
   * @fires "remove-file"
   */
  private dispatchFileRemove() {
    const customEvent = new CustomEvent(Constants.FILE_REMOVE, {
      detail: {
        data: { file: this.file, id: this.id },
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }

  /**
   * @fires "remove-existing-file"
   */
  private dispatchUploadedFileRemove() {
    const customEvent = new CustomEvent(Constants.EXISTING_FILE_REMOVE, {
      detail: {
        data: { file: this.file, id: this.id },
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }
}
